import * as React from 'react';
import { useTheme, Theme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';

import { PrivacyPolicyDialog } from './PrivacyPolicyDialog';
import { TermOfServiceDialog } from './TermOfServiceDialog';

interface FormFieldProps {
  formik: FormikProps<any>;
  displayTOS?: boolean;
  displayPrivacyPolicy?: boolean;
  displayNewsletter?: boolean;
  disabled?: boolean;
  disabledTOS?: boolean;
  disabledPrivacyPolicy?: boolean;
  disabledNewsletter?: boolean;
}

export const AcceptanceCheckboxes = React.memo(
  ({
    formik,
    displayTOS = true,
    displayPrivacyPolicy = true,
    displayNewsletter = true,
    disabled = false,
    disabledTOS = false,
    disabledPrivacyPolicy = false,
    disabledNewsletter = false,
  }: FormFieldProps) => {
    const { t } = useTranslation('field');
    const theme = useTheme();

    return (
      <Stack spacing={1}>
        {displayTOS && (
          <Stack
            direction="row"
            p={2}
            justifyContent="space-between"
            alignItems="center"
            sx={{
              backgroundColor: formik.values.isAcceptedTOS ? '#021900' : 'background.default',
              border: (theme: Theme) =>
                `1px solid ${formik.values.isAcceptedTOS ? '#0d7701' : theme.palette.grey[800]}`,
              borderRadius: 1,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {formik.values.isAcceptedTOS && (
                <theme.icons.check
                  color={formik.values.isAcceptedTOS ? 'success' : 'inherit'}
                  sx={{ fontSize: 20, marginTop: 0.5 }}
                />
              )}
              <Typography
                fontSize={{ xs: 14, sm: 16 }}
                color={formik.values.isAcceptedTOS ? 'text.primary' : 'text.secondary'}
              >
                {t('signup.stepper.I accept the ToS', { ns: 'authentication' }) + '*'}
              </Typography>
            </Stack>
            <TermOfServiceDialog formik={formik} disabled={disabled || disabledTOS} />
          </Stack>
        )}
        {displayPrivacyPolicy && (
          <Stack
            direction="row"
            p={2}
            justifyContent="space-between"
            alignItems="center"
            sx={{
              backgroundColor: formik.values.isAcceptedPrivacyPolicy ? '#021900' : 'background.default',
              border: (theme: Theme) =>
                `1px solid ${formik.values.isAcceptedPrivacyPolicy ? '#0d7701' : theme.palette.grey[800]}`,
              borderRadius: 1,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {formik.values.isAcceptedPrivacyPolicy && (
                <theme.icons.check
                  color={formik.values.isAcceptedPrivacyPolicy ? 'success' : 'inherit'}
                  sx={{ fontSize: 20, marginTop: 0.5 }}
                />
              )}
              <Typography
                fontSize={{ xs: 14, sm: 16 }}
                color={formik.values.isAcceptedPrivacyPolicy ? 'text.primary' : 'text.secondary'}
              >
                {t('signup.stepper.I accept the privacy policy', { ns: 'authentication' }) + '*'}
              </Typography>
            </Stack>
            <PrivacyPolicyDialog formik={formik} disabled={disabled || disabledPrivacyPolicy} />
          </Stack>
        )}
        {displayNewsletter && (
          <Stack
            direction="row"
            px={2}
            py={1}
            justifyContent="space-between"
            alignItems="center"
            sx={{
              backgroundColor: formik.values.isAcceptedNewsletter ? '#021900' : 'background.default',
              border: (theme: Theme) =>
                `1px solid ${formik.values.isAcceptedNewsletter ? '#0d7701' : theme.palette.grey[800]}`,
              borderRadius: 1,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  {...formik.getFieldProps('isAcceptedNewsletter')}
                  color="success"
                  disabled={disabled || disabledNewsletter}
                />
              }
              label={t('signup.stepper.I want to receive the newsletter', { ns: 'authentication' })}
            />
          </Stack>
        )}
      </Stack>
    );
  }
);
